import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { LowTonnageReason } from "@/pages/caster/models/catalogs/low-tonnage-reasons";
import { useModalStyles } from "@/pages/laboratory/styles/modalStyles";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid2 as Grid,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { Alert } from "@mui/material";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
	deleteLowTonnageReason,
	upsertLowTonnageReason,
} from "../repositories";

type Props = {
	open: boolean;
	mode: "create" | "edit" | "delete";
	lowTonnageReason?: LowTonnageReason | null;
	onClose: () => void;
	onSuccess: () => void;
};

export const UpsertLowTonnageReason: React.FC<Props> = ({
	open,
	mode,
	lowTonnageReason,
	onClose,
	onSuccess,
}) => {
	const classes = useModalStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [error, setError] = useState("");
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<LowTonnageReason>({
		defaultValues: {
			LowTonnageReasonId: 0,
			LowTonnageReasonName: "",
			IsActive: true,
		},
	});

	const title = useMemo(() => {
		if (mode === "edit") {
			return "EDIT LOW TONNAGE REASON";
		} else if (mode === "delete") {
			return "DELETE LOW TONNAGE REASON";
		} else if (mode === "create") {
			return "NEW LOW TONNAGE REASON";
		}
	}, [mode]);

	const handleSnackbarClose = (
		event: Event | SyntheticEvent<Element, Event>,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarOpen(false);
	};

	const deleteLowTonnageReasonAction = async (value?: string) => {
		if (value !== "OK") {
			setOpenDeleteDialog(false);
			return;
		}
		setOpenDeleteDialog(false);
		setIsSubmitLoading(true);

		const response = await deleteLowTonnageReason(
			lowTonnageReason?.LowTonnageReasonId || 0,
		);

		if (response.ok) {
			setSnackbarMessage("The low tonnage reason was deleted successfully");
			setSnackbarOpen(true);
			onSuccess();
			onClose();
		} else {
			setError(response.message);
		}
		setIsSubmitLoading(false);
	};

	const onSubmit: SubmitHandler<LowTonnageReason> = async (data: any) => {
		setIsSubmitLoading(true);

		if (mode === "delete") {
			setOpenDeleteDialog(true);
		} else {
			const response = await upsertLowTonnageReason(data);
			if (response.ok) {
				setSnackbarMessage(
					mode === "edit"
						? "The low tonnage reason was updated successfully"
						: "The low tonnage reason was created successfully",
				);
				setSnackbarOpen(true);
				onSuccess();
				onClose();
			} else {
				setError(response.message);
			}
		}

		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (mode === "edit" || mode === "create" || mode === "delete") {
			if (lowTonnageReason) {
				reset(lowTonnageReason);
			} else if (mode === "create") {
				reset({
					LowTonnageReasonId: 0,
					LowTonnageReasonName: "",
					IsActive: true,
				});
			}
		}
	}, [mode, lowTonnageReason, reset]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={onClose}
				maxWidth="md"
				aria-labelledby="contained-modal-title-vcenter"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						<Grid container spacing={1}>
							<Grid size={{ xs: 12, md: 12 }}>
								<TextField
									{...register("LowTonnageReasonName", {
										required: true,
										maxLength: 100, // VARCHAR(100) validation
									})}
									label="Low Tonnage Reason Name"
									variant="outlined"
									error={!!errors.LowTonnageReasonName}
									helperText={
										errors.LowTonnageReasonName?.type === "maxLength"
											? "Maximum length is 100 characters"
											: ""
									}
									fullWidth
									margin="dense"
									autoComplete="off"
									disabled={mode === "delete"}
								/>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={!!field.value}
											onCheckboxChange={field.onChange}
											label="Enabled"
											className={classes.checkbox}
											disabled={mode === "delete"}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={onClose}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color={mode === "delete" ? "error" : "primary"}
									type="submit"
								>
									{mode === "delete" ? "Delete" : "Save"}
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<ConfirmationDialogRaw
				title="Confirmation"
				message={`Are you sure you want to delete permanently the low tonnage reason: ${lowTonnageReason?.LowTonnageReasonName}?`}
				open={openDeleteDialog}
				onClose={deleteLowTonnageReasonAction}
			/>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={2500}
				onClose={handleSnackbarClose}
			>
				<Alert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleSnackbarClose}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
